import { algoliasearch } from "algoliasearch";
import { globals } from "@/core/globals";
import { reportAlgolia429 } from "@/core/utilities/application-insights/application-insights";
import {
  addSearchAnalyticsTags,
  addSearchAnalyticsTagsToParams,
} from "@/shared/thorlabs/search/utils/search-analytics-tags";

/** Locales for which an Algolia index exists. Any other locale falls back to "en". */
export const ALGOLIA_INDEX_LANGUAGES = ["en", "zh", "ja"] as const;

export const initializeSearchClient = (source: "search-page" | "new-products" = "search-page") => {
  const client = algoliasearch(globals.algoliaConfig.applicationId ?? "", globals.algoliaConfig.searchApiKey);
  const originalSearch = client.search.bind(client);
  const analyticsSource = source === "search-page" ? "search" : source;
  client.search = (async (...args: Parameters<typeof originalSearch>) => {
    try {
      const [params, requestOptions] = args;
      return await originalSearch(addSearchAnalyticsTags(params, analyticsSource), requestOptions);
    } catch (error) {
      reportAlgolia429(error, source);
      throw error;
    }
  }) as typeof client.search;
  const originalSearchSingleIndex = client.searchSingleIndex.bind(client);
  client.searchSingleIndex = <T>(...args: Parameters<typeof originalSearchSingleIndex>) => {
    const [params, requestOptions] = args;
    return originalSearchSingleIndex<T>(
      { ...params, searchParams: addSearchAnalyticsTagsToParams(params.searchParams, analyticsSource) },
      requestOptions,
    );
  };
  return client;
};

export const getIndexName = (indexType: string, language: string) => {
  const safeLanguage = (ALGOLIA_INDEX_LANGUAGES as readonly string[]).includes(language) ? language : "en";
  return `${globals.algoliaConfig.indexNamePrefix}${indexType}_${safeLanguage}`;
};

export const formatDate = (date: Date | string) => {
  if (!date) {
    return "";
  }

  const dateObject = date instanceof Date ? date : new Date(date);

  // Normalize the date to UTC and check if it's exactly 1900-01-01
  const utcYear = dateObject.getUTCFullYear();
  const utcMonth = dateObject.getUTCMonth();
  const utcDate = dateObject.getUTCDate();

  if (utcYear === 1900 && utcMonth === 0 && utcDate === 1) {
    return "";
  }

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  return dateObject.toLocaleDateString("en-US", options);
};
